import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import usersAPI from '../../utils/api/users';

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersAPI.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await usersAPI.create(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const reinviteUser = createAsyncThunk(
  'users/reinviteUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await usersAPI.reinvite(userId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ userId, ...payload }, { rejectWithValue }) => {
    try {
      const response = await usersAPI.update(userId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  usersList: [],
  loading: false,
  updateLoading: false,
  createLoading: false,
  reinviteLoading: false,
  error: null,
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearUsersError: (state) => {
      state.error = null;
    },
    clearUsersState: (state) => {
      state.usersList = [];
      state.loading = false;
      state.updateLoading = false;
      state.createLoading = false;
      state.reinviteLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.usersList = action.payload?.data || [];
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUser.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateLoading = false;
        const updated = action.payload?.user;
        if (!updated?.userId) return;
        const idx = state.usersList.findIndex((u) => u.userId === updated.userId);
        if (idx !== -1) state.usersList[idx] = updated;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(createUser.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createLoading = false;
        state.usersList.push(action.payload?.user);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      .addCase(reinviteUser.pending, (state) => {
        state.reinviteLoading = true;
        state.error = null;
      })
      .addCase(reinviteUser.fulfilled, (state, action) => {
        state.reinviteLoading = false;
        state.usersList = state.usersList.map((user) => user.userId === action.payload?.userId ? action.payload?.user : user);
      })
      .addCase(reinviteUser.rejected, (state, action) => {
        state.reinviteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUsersError, clearUsersState, clearReinviteUserState, clearDeleteUserState } = usersSlice.actions;
export default usersSlice.reducer;

